import { resolveStoredDocumentMimeType } from '@/utils/fileMime';

const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46, 0x2d] as const; // %PDF-

function asUint8Array(bytes: ArrayBuffer | ArrayBufferView): Uint8Array {
  const view =
    bytes instanceof ArrayBuffer
      ? new Uint8Array(bytes)
      : new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const copy = new Uint8Array(view.byteLength);
  copy.set(view);
  return copy;
}

export function hasPdfMagic(bytes: ArrayBuffer | ArrayBufferView): boolean {
  const view = asUint8Array(bytes);
  if (view.byteLength < PDF_MAGIC.length) return false;
  return PDF_MAGIC.every((value, index) => view[index] === value);
}

export function resolvePreviewMimeType(input: {
  mimeType?: string | null;
  filename?: string | null;
  bytes?: ArrayBuffer | ArrayBufferView | null;
}): string {
  if (input.bytes && hasPdfMagic(input.bytes)) return 'application/pdf';
  return resolveStoredDocumentMimeType(input);
}

export function previewBlobFromBytes(
  bytes: ArrayBuffer | ArrayBufferView,
  input: { mimeType?: string | null; filename?: string | null } = {},
): Blob {
  const view = asUint8Array(bytes);
  if (view.byteLength === 0) {
    throw new Error('preview_empty');
  }
  const resolved = resolvePreviewMimeType({ ...input, bytes: view });
  const mimeType =
    resolved === 'text/plain' ? 'text/plain;charset=utf-8' : resolved || 'application/octet-stream';
  const buffer = new ArrayBuffer(view.byteLength);
  new Uint8Array(buffer).set(view);
  return new Blob([buffer], { type: mimeType });
}

export async function previewBlobFromResponse(
  response: Response,
  input: { mimeType?: string | null; filename?: string | null } = {},
): Promise<Blob> {
  if (!response.ok) {
    throw new Error('preview_failed');
  }
  return previewBlobFromBytes(await response.arrayBuffer(), input);
}

export function previewBlobFromFile(file: File, mimeType?: string | null): Blob {
  if (file.size === 0) {
    throw new Error('preview_empty');
  }
  const resolved = resolveStoredDocumentMimeType({
    mimeType: mimeType || file.type,
    filename: file.name,
  });
  if (file.type === resolved) return file;
  return new Blob([file], { type: resolved || 'application/octet-stream' });
}
